import React, { createContext, useContext, useState, useEffect, useReducer, useCallback } from 'react';
import AddressView from './Components/AddressView';
import Warning from './Components/Warning';
import Error from './Components/Error';
import { ServiceClient } from '../../../../../Services/ServiceClient';
import LoaderComponent from '../../../../../Elements/ids/Loader';
import { PresentationMode } from '../../../../../Enums/PresentationMode';
import AddressButtons from './Components/Buttons';
import { addressReducer, addressReducerTypes } from './Reducer';
import Modal from '../../../../../Elements/ids/Modal/';
import { useGlobalContext } from '../../../../../context/GlobalContext';
import { useDebouncedEffect } from '../../../../../Helpers/CustomHooks';
import { areFieldsValid, hasAnyFieldChanged } from '../../../../../Helpers/AddressHelper';

const GlobalAddressContext = createContext(null);

export const useGlobalAddressContext = () => useContext(GlobalAddressContext);

const AddressInfo = ({ closeView, agentCode, codePrefix }) => {
  const { legalUnit } = useGlobalContext();

  const [canSubmit, setCanSubmit] = useState(false);
  const [modalMode, setModalMode] = useState(PresentationMode.Loading);
  const [countryList, setCountryList] = useState([]);

  const isModalOpenedOnTeamLevel = agentCode === null || agentCode === undefined;

  const [addressState, addressDispatch] = useReducer(addressReducer, {});

  const getCommonFields = (propertyName, isMandatory = false) => {
    return {
      name: propertyName,
      key: propertyName,
      error: addressState.errors[propertyName],
      isMandatory: isMandatory,
      value: addressState.address[propertyName] ?? '',
      onChange: event => {
        addressDispatch({
          type: addressReducerTypes.updateFieldValue,
          propertyName,
          value: event.target.value,
        });
      },
    };
  };

  const openErrorView = (countries, addressList) => {
    setModalMode(PresentationMode.Error);
    addressDispatch({
      type: addressReducerTypes.setAddressStateWhenError,
      legalUnit: legalUnit,
      countryList: countries,
      addressList: addressList,
    });
  };

  const validateAddressInfo = useCallback(() => {
    if (!addressState.address && modalMode !== PresentationMode.Add && modalMode !== PresentationMode.Edit) {
      return;
    }

    const validationResult = areFieldsValid(addressState.address);
    setCanSubmit(
      modalMode === PresentationMode.Edit
        ? validationResult && hasAnyFieldChanged(addressState.address, addressState.uneditedData?.address)
        : validationResult
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState.address, modalMode]);

  useDebouncedEffect(validateAddressInfo, 500);

  useEffect(() => {
    ServiceClient.getCountries(countries => {
      setCountryList(countries);
      ServiceClient.getAddressListForTeam(codePrefix, addressList => {
        if (addressList.length === 0) {
          openErrorView(countries, addressList);
        } else {
          ServiceClient.getAddressForTeam(codePrefix, teamAddressDto => {
            if (isModalOpenedOnTeamLevel) {
              if (teamAddressDto?.postalAddress) {
                addressDispatch({
                  type: addressReducerTypes.setInitialStateForTeam,
                  teamAddressDto,
                  addressList,
                });
                setModalMode(PresentationMode.View);
              } else {
                openErrorView(countries, addressList);
              }
            } else {
              ServiceClient.getAddressForAgent(
                agentCode,
                agentAddressDto => {
                  addressDispatch({
                    type: addressReducerTypes.setInitialStateForAgent,
                    agentAddressDto,
                    teamAddressDto,
                    addressList,
                  });
                  setModalMode(PresentationMode.View);
                },
                () => {
                  openErrorView(countries, addressList);
                }
              );
            }
          });
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentCode, codePrefix, isModalOpenedOnTeamLevel]);

  return (
    <Modal
      closeView={closeView}
      titleText={agentCode ?? codePrefix}
      buttons={
        <AddressButtons
          canSubmit={canSubmit}
          modalMode={modalMode}
          setModalMode={setModalMode}
          closeView={closeView}
          addressDispatch={addressDispatch}
          addressState={addressState}
        />
      }>
      <GlobalAddressContext.Provider
        value={{
          modalMode,
          setModalMode,
          isModalOpenedOnTeamLevel,
          addressState,
          addressDispatch,
          countryList,
          agentCode,
          codePrefix,
          getCommonFields,
        }}>
        {modalMode === PresentationMode.Loading ? (
          <LoaderComponent />
        ) : modalMode === PresentationMode.Warning ? (
          <Warning />
        ) : modalMode === PresentationMode.Error ? (
          <Error />
        ) : (
          <AddressView />
        )}
      </GlobalAddressContext.Provider>
    </Modal>
  );
};

export default AddressInfo;
